import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

export interface AppearanceMesh {
  meshes: THREE.Mesh[];
  scale: number[];
}
export interface Appearance {
  meshes: Map<string, AppearanceMesh>;
  neutralGeoms: Set<string>;
}

export function disposeAppearance(appearance: Appearance): void {
  const textures = new Set<THREE.Texture>();
  const materials = new Set<THREE.Material>();
  const geometries = new Set<THREE.BufferGeometry>();
  for (const { meshes } of appearance.meshes.values()) for (const mesh of meshes) {
    geometries.add(mesh.geometry);
    materials.add(mesh.material);
    for (const value of Object.values(mesh.material)) if (value instanceof THREE.Texture) textures.add(value);
  }
  for (const texture of textures) { texture.dispose(); texture.image?.close?.(); }
  for (const material of materials) material.dispose();
  for (const geometry of geometries) geometry.dispose();
}

/** Metalness for surfaces with no reflection data; shared so the two material paths cannot drift. */
export const NEUTRAL_METALNESS = 0.03;

export async function loadAppearance(url: string | null): Promise<Appearance> {
  const result: Appearance = { meshes: new Map(), neutralGeoms: new Set() };
  if (!url) return result;
  const response = await fetch(url, { cache: "force-cache" });
  if (!response.ok) throw new Error("Appearance metadata could not be loaded");
  const descriptor = await response.json() as { version: number; meshes: Record<string, { file: string; scale: number[] }>; neutral_geoms: string[] };
  if (descriptor.version !== 1) throw new Error("Unsupported appearance version");
  result.neutralGeoms = new Set(descriptor.neutral_geoms);
  const loader = new GLTFLoader();
  const entries = Object.entries(descriptor.meshes);
  let cursor = 0;
  async function worker(): Promise<void> {
    while (cursor < entries.length) {
      const [name, entry] = entries[cursor++]!;
      if (!entry.file.startsWith("appearance/") || entry.file.split("/").includes("..") || entry.file.includes("\\")) throw new Error("Invalid appearance resource");
      const glbUrl = new URL(entry.file, new URL(url!, location.href));
      const loaded = await loader.loadAsync(glbUrl.href);
      loaded.scene.updateMatrixWorld(true);
      const meshes: THREE.Mesh[] = [];
      loaded.scene.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        meshes.push(object);
        // glTF defaults metallicFactor to 1.0 and this viewer has no environment map, so a
        // material that neither sets it nor supplies a metalness map has nothing to reflect
        // and renders black -- while the same asset's 2D render, lit in Blender, looks right.
        // A material that genuinely declares 1.0 is equally unrenderable here, so both are
        // pulled to the value the MuJoCo material path already uses.
        for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
          if (material instanceof THREE.MeshStandardMaterial && material.metalness === 1 && !material.metalnessMap) {
            material.metalness = NEUTRAL_METALNESS;
          }
        }
      });
      result.meshes.set(name, { meshes, scale: entry.scale });
    }
  }
  const workers = await Promise.allSettled(Array.from({ length: Math.min(2, entries.length) }, () => worker()));
  const failure = workers.find((item) => item.status === "rejected");
  if (failure?.status === "rejected") { disposeAppearance(result); throw failure.reason; }
  return result;
}
